<template>
    <div class="w-full">
        <div
            class="mt-1 flex justify-between lg:flex-row flex-col"
            v-if="section_type == 'site_admin' || section_type == 'association' || section_type == 'company'"
        >
            <div class="w-auto">
                <a-input-search
                    data-cy="claim-input-search"
                    @search="onSearch"
                    v-model:value="search_value"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>
            <div class="pt-4 lg:pt-0">
                <a-button
                    data-cy="advance-search-show-button"
                    type="primary"
                    @click="onShowAdvanceSearch"
                    v-if="!is_advance_search_visible"
                >
                    {{ $t('claims.table.actions.show_advance_search') }}
                </a-button>
                <a-button
                    data-cy="advance-search-hide-button"
                    type="primary"
                    @click="onHideAdvanceSearch"
                    v-if="is_advance_search_visible"
                >
                    {{ $t('claims.table.actions.hide_advance_search') }}
                </a-button>
            </div>
        </div>
        <div class="w-full" v-if="is_advance_search_visible">
            <form-claim-admin-search
                :search_form="search_form"
                :section_type="section_type"
                :processing="processing"
                :validation="validation"
                @clear="handleFormClear"
                @formSubmit="handleFormSubmit"
            />
        </div>
        <div
            :class="{
                'pt-2 md:pt-4':
                    section_type == 'site_admin' || section_type == 'association' || section_type == 'company',
            }"
        >
            <div class="w-full">
                <a-table
                    data-cy="claims-list"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('claims.table.actions.view')">
                                    <a-button
                                        data-cy="edit-button"
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'view')"
                                    >
                                        <FolderOpenOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'claim_id_link'">
                            <router-link
                                :to="{ name: 'view-claim', params: { id: record.id } }"
                                class="text-default-purple underline hover:text-highlight-purple whitespace-nowrap"
                            >
                                {{ text }}
                            </router-link>
                        </template>
                        <template v-else-if="column.key === 'date_of_loss'">
                            <span class="whitespace-nowrap">
                                {{ text }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'cb_status'">
                            <span
                                class="font-semibold"
                                :class="claimStatus(text) === 'ACTIVE' ? 'text-default-green' : 'text-default-red'"
                            >
                                {{ claimStatus(text) }}
                            </span>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="pagination.total !== undefined" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('claims.table.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                    <span class="text-red-500 pl-1" v-if="showLimitTag">
                        {{
                            $t('claims.table.page_range_limit', {
                                limit: max_limit,
                            })
                        }}
                    </span>
                </span>
                <span
                    v-if="
                        (section_type == 'site_admin' ||
                            section_type == 'association' ||
                            section_type == 'cover' ||
                            section_type == 'company') &&
                        data.length > 0
                    "
                    data-cy="search-items-found"
                    class="font-semibold block pb-1 md:inline-block md:pb-0 md:pr-4"
                >
                    {{
                        $t('claims.search_form.search_results_split', {
                            motor_total: search_motor_count,
                            non_motor_total: search_non_motor_count,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import FormClaimAdminSearch from '@/components/forms/claims/AdminSearch.vue';
import { FolderOpenOutlined } from '@ant-design/icons-vue';

export default {
    components: {
        FormClaimAdminSearch,
        FolderOpenOutlined,
    },
    props: {
        section_type: {
            type: String,
            default: '',
        },
        cover_id: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            search_id: '',
            data: [],
            search_value: null,
            is_advance_search_visible: false,
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('claims.table.no_data'),
            },
            loading: false,
            current_filter: {},
            sorter: {},
            params: {},
            search_motor_count: 0,
            search_non_motor_count: 0,
            processing: false,
            max_limit: 0,
            search_form: {
                association_id: null,
                company_id: null,
                claim_no: null,
                policy_no: null,
                name: '',
                national_id: '',
                driver_lic_no: '',
                claim_year: null,
                reg_no: '',
                chassis_no: '',
                engine_no: '',
                risk_type: '',
                class_of_business: '',
                theft_related: '',
                claim_status: '',
                risk_location: '',
                cause_of_loss: '',
                loss_date_range: {
                    start_date: null,
                    end_date: null,
                },
                date_imported_range: {
                    start_date: null,
                    end_date: null,
                },
                file_source: '',
            },
            validation: {
                errors: {},
                message: '',
            },
        };
    },
    mounted() {
        if (
            this.section_type == 'site_admin' ||
            this.section_type == 'association' ||
            this.section_type == 'cover' ||
            this.section_type == 'company'
        ) {
            this.fetch();
        }
    },
    computed: {
        getColumns() {
            if (
                this.section_type == 'site_admin' ||
                this.section_type == 'association' ||
                this.section_type == 'company'
            ) {
                return [
                    {
                        key: 'actions',
                        width: 35,
                    },
                    {
                        title: this.$t('claims.table.headers.claim_no'),
                        key: 'claim_id',
                        dataIndex: 'claim_id',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.loss_data'),
                        key: 'date_of_loss',
                        dataIndex: 'date_of_loss',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.class'),
                        key: 'class_of_business',
                        dataIndex: 'class_of_business',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.insured'),
                        key: 'insured_name',
                        dataIndex: 'insured_name',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.cause'),
                        key: 'cause_of_accident',
                        dataIndex: 'cause_of_accident',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.insurer'),
                        key: 'company_code',
                        dataIndex: 'company_code',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.status'),
                        key: 'cb_status',
                        dataIndex: 'cb_status',
                        sorter: true,
                    },
                ];
            } else {
                return [
                    {
                        title: this.$t('claims.table.headers.claim_no'),
                        key: 'claim_id_link',
                        dataIndex: 'claim_id',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.loss_data'),
                        key: 'date_of_loss',
                        dataIndex: 'date_of_loss',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.class'),
                        key: 'class_of_business',
                        dataIndex: 'class_of_business',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.insured'),
                        key: 'insured_name',
                        dataIndex: 'insured_name',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.cause'),
                        key: 'cause_of_accident',
                        dataIndex: 'cause_of_accident',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.insurer'),
                        key: 'company_code',
                        dataIndex: 'company_code',
                        sorter: true,
                    },
                    {
                        title: this.$t('claims.table.headers.status'),
                        key: 'cb_status',
                        dataIndex: 'cb_status',
                        sorter: true,
                    },
                ];
            }
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        showLimitTag() {
            return (
                this.pagination.total !== undefined &&
                this.max_limit > 0 &&
                this.pagination.total > this.max_limit &&
                !this.is_advance_search_visible
            );
        },
    },
    methods: {
        claimStatus(status) {
            if (status !== '' && status !== null && status !== undefined) {
                return status.toUpperCase();
            }

            return '';
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}, search_id = '') {
            if (search_id !== '') {
                this.search_id = search_id;
            }

            this.loading = true;
            this.params = params;

            this.fetchData();
            this.fetchDelayedData();
        },
        fetchData() {
            let params = this.params;
            let urls = [];

            switch (this.section_type) {
                case 'site_admin':
                    urls = [
                        this.$http.get('/api/admin/claims/search', {
                            params: params,
                        }),
                        this.$http.get('/api/admin/claims/search-count', {
                            params: params,
                        }),
                    ];
                    break;
                case 'association':
                    urls = [
                        this.$http.get('/api/associations/claims/search', {
                            params: params,
                        }),
                        this.$http.get('/api/associations/claims/search-count', {
                            params: params,
                        }),
                    ];
                    break;
                case 'company':
                    params.company_id = this.$store.getters['user/currentCompanyId'];
                    params.association_id = this.$store.getters['user/currentAssociationId'];

                    urls = [
                        this.$http.get('/api/companies/claims/search', {
                            params: params,
                        }),
                        this.$http.get('/api/companies/claims/search-count', {
                            params: params,
                        }),
                    ];
                    break;
                case 'cover':
                    urls = [
                        this.$http.get('/api/claims/get-by-cover/' + this.cover_id, {
                            params: params,
                        }),
                        this.$http.get('/api/claims/get-by-cover/search-count/' + this.cover_id, {
                            params: params,
                        }),
                    ];
                    break;
                default:
                    if (this.$store.getters['user/isMultiAssociation']) {
                        params.association_id = this.$store.getters['user/currentAssociationId'];
                    }

                    urls = [
                        this.$http.get('/api/claims/search/' + this.search_id, {
                            params: params,
                        }),
                        this.$http.get('/api/claims/search-count/' + this.search_id, {
                            params: params,
                        }),
                    ];
                    break;
            }

            this.$http
                .all(urls)
                .then(
                    this.$http.spread((response, count) => {
                        const pagination = { ...this.pagination };

                        // Read total count from server
                        pagination.total = count.data.meta.total_count;

                        this.data = response.data.data;
                        this.pagination = pagination;
                        this.max_limit = count.data.meta.max_limit;

                        let search_criteria = {};

                        if (response.data.search_criteria !== null) {
                            search_criteria = response.data.search_criteria;
                        }

                        this.$emit('fetch', {
                            search_count: pagination.total,
                            search_criteria: search_criteria,
                        });

                        this.$emit('clear', false);
                    }),
                )
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCLMF01' }),
                        });
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        fetchDelayedData() {
            let params = this.params;
            let url = '';

            switch (this.section_type) {
                case 'site_admin':
                    url = '/api/admin/claims/search-split-count';
                    break;
                case 'association':
                    url = '/api/associations/claims/search-split-count';
                    break;
                case 'company':
                    url = '/api/companies/claims/search-split-count';
                    break;
                case 'cover':
                    url = '/api/claims/get-by-cover/search-split-count/' + this.cover_id;
                    break;
                default:
                    if (this.$store.getters['user/isMultiAssociation']) {
                        params.association_id = this.$store.getters['user/currentAssociationId'];
                    }

                    url = '/api/claims/search-split-count/' + this.search_id;
                    break;
            }

            this.$http
                .get(url, {
                    params: params,
                })
                .then((response) => {
                    this.$emit('fetchSplitCount', {
                        search_motor_count: response.data.meta.total_motor,
                        search_non_motor_count: response.data.meta.total_non_motor,
                    });

                    this.search_motor_count = response.data.meta.total_motor;
                    this.search_non_motor_count = response.data.meta.total_non_motor;
                })
                .catch((error) => {
                    if (error.response.status == 403) {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.403_mod'),
                        });
                    } else {
                        this.$notification.error({
                            message: this.$t('globals.global.notifications.error.any', { code: 'CVCLMF02' }),
                        });
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9'\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                this.fetch({
                    basic_search: this.search_value,
                });
                this.pagination.current = 1;
            } else {
                this.search_value = null;

                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        onShowAdvanceSearch() {
            this.handleFormClear();
            this.is_advance_search_visible = true;
        },
        onHideAdvanceSearch() {
            this.is_advance_search_visible = false;
        },
        onTableAction(id, key) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
            });
        },
        handleFormClear() {
            this.search_form = {
                association_id: null,
                company_id: null,
                claim_no: null,
                policy_no: null,
                name: '',
                national_id: '',
                driver_lic_no: '',
                claim_year: null,
                reg_no: '',
                chassis_no: '',
                engine_no: '',
                risk_type: '',
                class_of_business: '',
                theft_related: '',
                claim_status: '',
                risk_location: '',
                cause_of_loss: '',
                loss_date_range: {
                    start_date: null,
                    end_date: null,
                },
                date_imported_range: {
                    start_date: null,
                    end_date: null,
                },
                file_source: '',
            };
        },
        handleFormSubmit() {
            let payload = {};
            this.search_value = null;

            _.forEach(this.search_form, function (value, key) {
                if (typeof value === 'object' && value !== null) {
                    if (
                        (value.start_date !== undefined && value.start_date !== null) ||
                        (value.end_date !== undefined && value.end_date !== null)
                    ) {
                        payload[key] = {
                            start_date:
                                value.start_date !== undefined && value.start_date !== null
                                    ? value.start_date.format('YYYY-MM-DD')
                                    : null,
                            end_date:
                                value.end_date !== undefined && value.end_date !== null
                                    ? value.end_date.format('YYYY-MM-DD')
                                    : null,
                        };
                    }
                } else if (value !== null && value !== '') {
                    payload[key] = value;
                }
            });

            this.fetch(payload);
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
    },
};
</script>
